import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { ErrorHandlerService } from './error-handler.service';
import { IntegrationsService } from './integrations.service';
import { OAuthService } from './oauth.service';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NotificationService } from './notification.service';
import { LoggedUserReport } from '../../shared/models/user';
import { AuthProvider } from '../../shared/models/integrations.model';
import { AuthBasicModalComponent } from '../../shared/components/auth-basic-modal/auth-basic-modal.component';
import { AuthOauth2ModalComponent } from '../../shared/components/auth-oauth2-modal/auth-oauth2-modal.component';

@Injectable({
  providedIn: 'root'
})
export class DataSourceProvidersService {
  private readonly baseUrl = environment.baseBackendUrl;

  constructor(
    private http: HttpClient,
    private errorHandlerService: ErrorHandlerService,
    private integrationsService: IntegrationsService,
    private oauthService: OAuthService,
    private modalService: NzModalService,
    private notificationService: NotificationService
  ) {
  }

  /**
   * Verifica las credenciales de todos los DataSourceProviders de un reporte
   * @param report Reporte con DataSourceProviders a verificar
   * @param onSuccess Callback que se ejecuta si todas las credenciales son válidas
   * @param retryCallback Callback que se ejecuta después de configurar credenciales (para reintentar)
   * @returns Promise<boolean> true si todas las credenciales son válidas, false en caso contrario
   */
  async checkReportCredentials(
    report: any, // Acepta cualquier objeto que tenga dataSourceProviders
    onSuccess?: () => void,
    retryCallback?: () => void
  ): Promise<boolean> {
    if (!report.dataSourceProviders || report.dataSourceProviders.length === 0) {
      // No hay DataSourceProviders, está OK
      if (onSuccess) onSuccess();
      return true;
    }

    for (const dataSourceProvider of report.dataSourceProviders) {
      try {
        const response = await this.integrationsService.checkCredentialsStatus(dataSourceProvider.id).toPromise();

        if (!response || !response.isValid) {
          // Credenciales inválidas, mostrar modal apropiado
          await this.handleInvalidCredentials(report, dataSourceProvider, retryCallback);
          return false;
        }
      } catch (error) {
        // Error al verificar credenciales
        await this.handleInvalidCredentials(report, dataSourceProvider, retryCallback);
        return false;
      }
    }

    // Todas las credenciales son válidas
    if (onSuccess) onSuccess();
    return true;
  }

  /**
   * Método centralizado para verificar credenciales con gestión de estado
   * Elimina la duplicación de código en los componentes de reportes
   */
  async checkReportCredentialsWithStateManagement(
    report: any,
    onSuccess?: () => void,
    onStateUpdate?: (state: { isValid: boolean; isChecked: boolean; isChecking: boolean }) => void
  ): Promise<boolean> {
    if (!report.dataSourceProviders || report.dataSourceProviders.length === 0) {
      if (onSuccess) onSuccess();
      if (onStateUpdate) onStateUpdate({ isValid: true, isChecked: true, isChecking: false });
      return true;
    }

    if (onStateUpdate) onStateUpdate({ isValid: false, isChecked: false, isChecking: true });

    for (const dataSourceProvider of report.dataSourceProviders) {
      try {
        const response = await this.integrationsService.checkCredentialsStatus(dataSourceProvider.id).toPromise();

        if (!response || !response.isValid) {
          await this.handleInvalidCredentialsWithStateManagement(report, dataSourceProvider, onSuccess, onStateUpdate);
          if (onStateUpdate) onStateUpdate({ isValid: false, isChecked: true, isChecking: false });
          return false;
        }
      } catch (error) {
        await this.handleInvalidCredentialsWithStateManagement(report, dataSourceProvider, onSuccess, onStateUpdate);
        if (onStateUpdate) onStateUpdate({ isValid: false, isChecked: true, isChecking: false });
        return false;
      }
    }

    if (onSuccess) onSuccess();
    if (onStateUpdate) onStateUpdate({ isValid: true, isChecked: true, isChecking: false });
    return true;
  }

  /**
   * Maneja credenciales inválidas abriendo el modal apropiado
   */
  private async handleInvalidCredentials(
    report: any, // Acepta cualquier objeto que tenga name/dataSourceProviders
    dataSourceProvider: AuthProvider,
    retryCallback?: () => void
  ): Promise<void> {
    return new Promise<void>((resolve) => {
      if (dataSourceProvider.authMethod === 'OAuth2') {
        // Abrir modal OAuth2
        const modalRef = this.modalService.create({
          nzContent: AuthOauth2ModalComponent,
          nzData: {
            dataSourceProvider: dataSourceProvider
          },
          nzTitle: '',
          nzFooter: null,
          nzCentered: true,
          nzWidth: 400,
          nzClosable: false,
          nzMask: true,
          nzMaskClosable: false,
          nzMaskStyle: {
            'backdrop-filter': 'blur(4px)',
            '-webkit-backdrop-filter': 'blur(4px)'
          }
        });

        // El modal OAuth2 redirige automáticamente, así que no esperamos a que se cierre
        resolve();
      } else {
        // Abrir modal Basic Auth
        const modalRef = this.modalService.create({
          nzContent: AuthBasicModalComponent,
          nzData: {
            dataSourceProvider: dataSourceProvider,
            report: report,
            onCredentialsConfigured: () => {
              // Ejecutar callback de reintento si existe
              if (retryCallback) {
                retryCallback();
              }
            }
          },
          nzTitle: '',
          nzFooter: null,
          nzCentered: true,
          nzWidth: 486,
          nzClosable: true,
          nzMask: true,
          nzMaskStyle: {
            'backdrop-filter': 'blur(4px)',
            '-webkit-backdrop-filter': 'blur(4px)'
          }
        });

        // Escuchar el cierre del modal
        modalRef.afterClose.subscribe(() => {
          resolve();
        });
      }
    });
  }

  /**
   * Maneja credenciales inválidas con gestión de estado para el método centralizado
   */
  private async handleInvalidCredentialsWithStateManagement(
    report: any,
    dataSourceProvider: AuthProvider,
    onSuccess?: () => void,
    onStateUpdate?: (state: { isValid: boolean; isChecked: boolean; isChecking: boolean }) => void
  ): Promise<void> {
    return new Promise<void>((resolve) => {
      if (dataSourceProvider.authMethod === 'OAuth2') {
        // Abrir modal OAuth2
        const modalRef = this.modalService.create({
          nzContent: AuthOauth2ModalComponent,
          nzData: {
            dataSourceProvider: dataSourceProvider
          },
          nzTitle: '',
          nzFooter: null,
          nzCentered: true,
          nzWidth: 400,
          nzClosable: false,
          nzMask: true,
          nzMaskClosable: false,
          nzMaskStyle: {
            'backdrop-filter': 'blur(4px)',
            '-webkit-backdrop-filter': 'blur(4px)'
          }
        });

        // El modal OAuth2 redirige automáticamente, así que no esperamos a que se cierre
        resolve();
      } else {
        // Abrir modal Basic Auth
        const modalRef = this.modalService.create({
          nzContent: AuthBasicModalComponent,
          nzData: {
            dataSourceProvider: dataSourceProvider,
            report: report,
            onCredentialsConfigured: () => {
              // Reintentar verificación de credenciales con gestión de estado
              setTimeout(() => {
                this.checkReportCredentialsWithStateManagement(report, onSuccess, onStateUpdate);
              }, 500);
            }
          },
          nzTitle: '',
          nzFooter: null,
          nzCentered: true,
          nzWidth: 486,
          nzClosable: true,
          nzMask: true,
          nzMaskStyle: {
            'backdrop-filter': 'blur(4px)',
            '-webkit-backdrop-filter': 'blur(4px)'
          }
        });

        // Escuchar el cierre del modal
        modalRef.afterClose.subscribe(() => {
          resolve();
        });
      }
    });
  }
}
